import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { AppShell } from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import SetupPage from "@/pages/SetupPage";
import WizardPage from "@/pages/WizardPage";
import TeamPage from "@/pages/TeamPage";
import ProjectPage from "@/pages/ProjectPage";
import TenderPage from "@/pages/TenderPage";
import PortfolioTowerPage from "@/pages/PortfolioTowerPage";
import MethodCalibratePage from "@/pages/MethodCalibratePage";
import StandardsPage from "@/pages/StandardsPage";
import SurvivalPage from "@/pages/SurvivalPage";
import AuditPage from "@/pages/AuditPage";
import FieldWorkPage from "@/pages/FieldWorkPage";
import WastePage from "@/pages/WastePage";
import SubPortalPage from "@/pages/SubPortalPage";
import CampaignLandingPage from "@/pages/campaign/CampaignLandingPage";
import CampaignConfirmPage from "@/pages/campaign/CampaignConfirmPage";
import CampaignProfilePage from "@/pages/campaign/CampaignProfilePage";
import LeadQueuePage from "@/pages/campaign/LeadQueuePage";
import CampaignUnsubscribePage from "@/pages/campaign/CampaignUnsubscribePage";

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-sage-light border-t-forest-mid" />
        <p className="text-sm text-muted-foreground">Loading ROOTS…</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.role === "waste_subcontractor") return <SubPortalPage />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            {/* Hidden LIASROOTS campaign module — public pages + admin-gated lead queue */}
            <Route path="/liasroots" element={<CampaignLandingPage />} />
            <Route path="/liasroots/confirmation" element={<CampaignConfirmPage />} />
            <Route path="/liasroots/profile/:token" element={<CampaignProfilePage />} />
            <Route path="/liasroots/unsubscribe/:token" element={<CampaignUnsubscribePage />} />
            <Route path="/liasroots/leads" element={<Protected><LeadQueuePage /></Protected>} />
            <Route
              path="/field"
              element={
                <Protected>
                  <WorkspaceProvider>
                    <FieldWorkPage />
                  </WorkspaceProvider>
                </Protected>
              }
            />
            <Route
              path="/*"
              element={
                <Protected>
                  <WorkspaceProvider>
                    <AppShell>
                      <Routes>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/setup/wizard" element={<WizardPage />} />
                        <Route path="/setup" element={<SetupPage />} />
                        <Route path="/project" element={<ProjectPage />} />
                        <Route path="/tower" element={<PortfolioTowerPage />} />
                        <Route path="/method" element={<MethodCalibratePage />} />
                        <Route path="/tender" element={<TenderPage />} />
                        <Route path="/standards" element={<StandardsPage />} />
                        <Route path="/survival" element={<SurvivalPage />} />
                        <Route path="/waste" element={<WastePage />} />
                        <Route path="/team" element={<TeamPage />} />
                        <Route path="/audit" element={<AuditPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </AppShell>
                  </WorkspaceProvider>
                </Protected>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
